import { useCallback } from "react";
import type {
  CheckoutRequest,
  CheckoutResponse,
  PlansResponse,
  PortalResponse,
  Subscription,
} from "../types/billing.ts";
import { useApiMutation } from "./use-api-mutation.ts";
import { useApiQuery } from "./use-api-query.ts";

/**
 * Hook to fetch available billing plans.
 */
export function useBillingPlans() {
  return useApiQuery<PlansResponse>("/billing/plans", {
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch current subscription status.
 */
export function useSubscription() {
  return useApiQuery<Subscription>("/billing/subscription", {
    refetchInterval: 30_000, // Refresh every 30 seconds
  });
}

/**
 * Hook to create a checkout session for a new subscription.
 */
export function useCreateCheckout() {
  const mutation = useApiMutation<CheckoutRequest, CheckoutResponse>("POST", "/billing/checkout");

  const checkout = useCallback(
    async (planKey: string) => {
      const result = await mutation.mutateAsync({ plan_key: planKey });
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
      return result;
    },
    [mutation],
  );

  return {
    ...mutation,
    checkout,
  };
}

/**
 * Hook to create a customer portal session for subscription management.
 */
export function useCreatePortal() {
  const mutation = useApiMutation<Record<string, never>, PortalResponse>("POST", "/billing/portal");

  const openPortal = useCallback(async () => {
    const result = await mutation.mutateAsync({});
    if (result.portal_url) {
      window.location.href = result.portal_url;
    }
    return result;
  }, [mutation]);

  return {
    ...mutation,
    openPortal,
  };
}

interface UpdatePlanRequest {
  plan_key: string;
}

interface UpdatePlanResponse {
  success: boolean;
  message: string;
}

/**
 * Hook to update an existing subscription to a new plan (upgrade/downgrade).
 */
export function useUpdatePlan() {
  const mutation = useApiMutation<UpdatePlanRequest, UpdatePlanResponse>(
    "POST",
    "/billing/update-plan",
  );

  const updatePlan = useCallback(
    async (planKey: string) => {
      return await mutation.mutateAsync({ plan_key: planKey });
    },
    [mutation],
  );

  return {
    ...mutation,
    updatePlan,
  };
}
